import React from 'react';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import SpacerRow from '@zg-rentals/multibrand-ui/lib/SpacerRow';
import CurrencyInput from '@zg-rentals/multibrand-ui/lib/CurrencyInput';
import Label from '@zg-rentals/multibrand-ui/lib/Label';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import { addMonths } from 'date-fns';
import { coerceToDate, formatDate } from '@common/paymentsUtils/dateUtils';
import { subMonths } from 'date-fns';
import { differenceInCalendarDays } from 'date-fns';
import SpinnerContainer from '@common/components/SpinnerContainer';
import FaqComponent from '@common/components/FaqComponent';
import faqs from '@common/constants/faqs';
import { centsToDollars, dollarsToCents } from '@common/paymentsUtils/currencyUtils';
import { RENT_AMOUNT_CHANGE_DROPDOWN_MAX, validateAllInputs } from '../validation';
import DebouncedButton from '@common/components/DebouncedButton';

type SubmissionPageProps = {
  today: Date;
  prevRentAmount: string;
  nextAllowedChangeDate: string;
  toggleNextPage: () => void;
  onSubmit: (...args: any) => any;
  onClose: () => void;
  maxMonthsForRentAmountChange?: number;
  trackValidationError?: (...args: any) => any;
};

type SubmissionPageState = {
  isLoading: boolean;
  rentAmount: { value: string; error: string };
  selectOptions: React.ReactNode[];
  selectedDate: { value: string; error: string };
};

class SubmissionPage extends React.Component<SubmissionPageProps, SubmissionPageState> {
  static defaultProps = {
    toggleNextPage: () => {},
    onSubmit: () => {},
    onClose: () => {},
  };

  constructor(props) {
    super(props);
    const { prevRentAmount, nextAllowedChangeDate, maxMonthsForRentAmountChange } = props;
    const selectOptions = this.getSelectOptions({ nextAllowedChangeDate, maxMonthsForRentAmountChange });

    this.state = {
      isLoading: false,
      rentAmount: {
        value: prevRentAmount ? centsToDollars(prevRentAmount) : '',
        error: '',
      },
      selectOptions,
      selectedDate: {
        value: nextAllowedChangeDate,
        error: '',
      },
    };
  }

  getSelectOptions = ({ nextAllowedChangeDate, maxMonthsForRentAmountChange }): React.ReactNode[] => {
    const selectOptions: React.ReactNode[] = [];
    const maxNumberOfDropdowns = Math.min(maxMonthsForRentAmountChange, RENT_AMOUNT_CHANGE_DROPDOWN_MAX);

    for (let i = 0; i < maxNumberOfDropdowns; i++) {
      const dueDate = formatDate(addMonths(coerceToDate(nextAllowedChangeDate)!, i), 'yyyy-MM-dd');
      selectOptions.push(
        <option key={dueDate} value={dueDate}>
          {formatDate(dueDate, 'MMMM dd, yyyy')}
        </option>,
      );
    }

    return selectOptions;
  };

  onChangeRentAmount = (e): void => {
    const newRentAmount = e.target.value;
    this.setState({
      rentAmount: {
        value: newRentAmount,
        error: '',
      },
    });
  };

  onChangeSelectedDate = (e): void => {
    const newSelectedDate = e.target.value;
    this.setState({
      selectedDate: {
        value: newSelectedDate,
        error: '',
      },
    });
  };

  toggleNextPage = () => this.props.toggleNextPage();

  onSubmit = async () => {
    const { rentAmount, selectedDate } = this.state;

    // show loading spinner
    this.setState({ isLoading: true });

    // validate inputs
    const validInputs = validateAllInputs(this);

    // if inputs not valid, return without hitting api
    if (!validInputs) {
      this.setState({ isLoading: false });
      return;
    }

    // api call
    const res = await this.props.onSubmit({
      rentAmount: dollarsToCents(rentAmount.value),
      effectiveDate: formatDate(selectedDate.value, 'yyyy-MM-dd'),
    });

    // if success, show confirmation screen. else stop loading spinner
    if (res && res.success) {
      this.toggleNextPage();
    } else {
      this.setState({ isLoading: false });
    }
  };

  onClose = (): void => this.props.onClose();

  render(): React.ReactNode {
    const { rentAmount, selectedDate, selectOptions, isLoading } = this.state;
    const { today, nextAllowedChangeDate } = this.props;

    const showFaq = Math.abs(differenceInCalendarDays(coerceToDate(nextAllowedChangeDate)!, today)) > 30;

    return (
      <>
        {isLoading && <SpinnerContainer />}
        <Text type="h3">What&apos;s your new monthly rent?</Text>
        <SpacerRow>
          <Label>Monthly amount</Label>
          <CurrencyInput
            data-statekey="rentAmount"
            noLabel
            value={rentAmount.value}
            error={rentAmount.error}
            onChange={this.onChangeRentAmount}
            onBlur={this.onChangeRentAmount}
            onFocus={this.onChangeRentAmount}
            data-auto-test-id="monthly-amount-input"
          />
        </SpacerRow>
        <SpacerRow>
          <Label data-auto-test-id="payment-end-date-label">Start date</Label>
          <Select
            data-id="selectedDate"
            value={selectedDate.value}
            error={selectedDate.error}
            onChange={this.onChangeSelectedDate}
          >
            {selectOptions}
          </Select>
        </SpacerRow>
        {showFaq && (
          <>
            <SpacerRow>
              <Text type="body-small">
                <strong>{formatDate(nextAllowedChangeDate, 'MMMM d, yyyy')}</strong> is the next eligible date for your
                rent change to take effect. Your tenant&apos;s&nbsp;
                {formatDate(subMonths(coerceToDate(nextAllowedChangeDate)!, 1), 'MMMM')} payment is already paid or on
                its way.
              </Text>
            </SpacerRow>
            <SpacerRow>
              <FaqComponent.V1
                title={faqs.LANDLORD_RENT_AMOUNT_CHANGE.title}
                faqs={faqs.LANDLORD_RENT_AMOUNT_CHANGE.body(formatDate(nextAllowedChangeDate, 'MMMM do'))}
                boxed
              />
            </SpacerRow>
          </>
        )}
        <SpacerRow>
          <DebouncedButton type="primary" full onClick={this.onSubmit}>
            Update rent amount
          </DebouncedButton>
          <SpacerRow size="sm" />
          <Button type="tertiary" full onClick={this.onClose}>
            Cancel
          </Button>
        </SpacerRow>
      </>
    );
  }
}

export default SubmissionPage;
